import { focusManager } from '@tanstack/react-query'
import { ehRedeBloqueada } from '@/lib/api/errors'
import { estaBloqueada, ouvirRedeBloqueada } from './redeBloqueada'

/**
 * Faixa 28 D6: com o bloqueio de rede aceso, nenhuma consulta TanStack nova sai. Ver `pausaDasConsultas.md`.
 *
 * O meio é o `focusManager`: bloqueada, a aba conta como "sem foco". No query-core 5.101 isso para o intervalo
 * (`queryObserver.ts` — o tick só busca com foco ou `refetchIntervalInBackground`), segura a retentativa já agendada
 * em `fetchStatus: 'paused'` sem virar erro (`retryer.ts::canContinue` exige foco em QUALQUER `networkMode`) e
 * impede o refetch de foco. Mutation não passa por foco ao começar (`retryer.ts::canStart`), então não muda. O
 * `onlineManager` foi descartado: pausa mutation (doc "Network Mode") e não segura `offlineFirst` (`canFetch`).
 */

/** O padrão do TanStack (`retryer.ts`: 3 no cliente). */
const RETENTATIVAS = 3

/** `retry` padrão das consultas: 403 `NETWORK_FORBIDDEN` não se conserta repetindo; o resto segue o padrão. */
export function retentarConsulta(failureCount: number, error: unknown): boolean {
  if (ehRedeBloqueada(error)) return false
  return failureCount < RETENTATIVAS
}

/**
 * `refetchOnReconnect` padrão das consultas: o evento `online` (`onlineManager` → `QueryClient` → `query.onOnline`)
 * não passa pelo foco, então a pausa do `focusManager` não o segura. Bloqueada → `false` (não refaz); liberada →
 * `true`, que no query-core 5.101.2 é o mesmo que o padrão: `shouldFetchOn` só refaz se stale
 * (`queryObserver.ts:784-786`: `value === 'always' || (value !== false && isStale(...))`).
 */
export const refazerAoReconectar = (): boolean => !estaBloqueada()

function abaVisivel(): boolean {
  return globalThis.document?.visibilityState !== 'hidden'
}

/** O `setup` padrão do `focusManager` (só `visibilitychange`), para devolver ao desligar. */
function focoPadrao(aoMudar: (focada?: boolean) => void): () => void {
  const ouvir = () => aoMudar()
  window.addEventListener('visibilitychange', ouvir, false)
  return () => window.removeEventListener('visibilitychange', ouvir)
}

/** Liga a pausa: foco = aba visível E rede não bloqueada. Devolve o desligar (volta ao padrão). Só no browser. */
export function ligarPausaDasConsultas(): () => void {
  if (typeof window === 'undefined') return () => {}
  focusManager.setEventListener((aoMudar) => {
    const avaliar = () => aoMudar(abaVisivel() && !estaBloqueada())
    window.addEventListener('visibilitychange', avaliar, false)
    const pararDeOuvirBloqueio = ouvirRedeBloqueada(avaliar)
    avaliar()
    return () => {
      window.removeEventListener('visibilitychange', avaliar)
      pararDeOuvirBloqueio()
    }
  })
  return () => {
    focusManager.setEventListener(focoPadrao)
    focusManager.setFocused(undefined)
  }
}
